import { useEffect, useState, type PropsWithChildren, type ReactNode } from 'react';
import { usePlatform } from '@forq/platform';
import { emptyUiLayout, textOverride, type UiLayout } from '@forq/platform-data';
import { useCustomize } from '../customize/CustomizeContext';

interface WorkspacePageProps extends PropsWithChildren {
  /** Small uppercase context label above the title. */
  readonly eyebrow?: string;
  /** Main page title. */
  readonly title: string;
  /** Optional one-line description under the title. */
  readonly description?: string;
  /** Primary action(s), rendered top-right of the header. */
  readonly actions?: ReactNode;
  /** Metric tiles, rendered as a row under the header. */
  readonly metrics?: ReactNode;
  /** Filter/search controls, rendered above the body. */
  readonly toolbar?: ReactNode;
  /**
   * Stabiler Seiten-Schlüssel: aktiviert das mandantenweite Umbenennen von Titel + Eyebrow im globalen
   * Bearbeiten-Modus (Frontend-Customizer). Ohne diesen Schlüssel verhält sich die Seite wie bisher.
   */
  readonly customizeKey?: string;
}

const TERMS_PAGE_KEY = 'terms';

/**
 * Standard skeleton every FORQ workspace shares: header → metrics → toolbar → body.
 * Gibt Modulen eine konsistente Form. Mit `customizeKey` werden Titel + Eyebrow im Bearbeiten-Modus je
 * Mandant umbenennbar (in dieselbe `terms`-jsonb wie die Navigations-Begriffe, keys `title:`/`eyebrow:`).
 */
export function WorkspacePage({
  eyebrow,
  title,
  description,
  actions,
  metrics,
  toolbar,
  customizeKey,
  children,
}: WorkspacePageProps) {
  const platform = usePlatform();
  const { tenantId, gateway } = useCustomize();
  const editing = !!customizeKey && platform.customizing && platform.permissions.has('admin.view');
  const [terms, setTerms] = useState<UiLayout>(emptyUiLayout);

  useEffect(() => {
    if (!customizeKey) return;
    let active = true;
    gateway.loadLayout(tenantId, TERMS_PAGE_KEY).then((loaded) => { if (active) setTerms(loaded); }).catch(() => {});
    return () => { active = false; };
  }, [gateway, tenantId, customizeKey]);

  const titleKey = `title:${customizeKey ?? ''}`;
  const eyebrowKey = `eyebrow:${customizeKey ?? ''}`;
  const shownTitle = customizeKey ? textOverride(terms, titleKey, title) : title;
  const shownEyebrow = customizeKey && eyebrow != null ? textOverride(terms, eyebrowKey, eyebrow) : eyebrow;

  // Overrides sofort speichern: nur echte Abweichungen; leer/unverändert = Standard.
  const saveTerm = (key: string, value: string, fallback: string) => {
    const trimmed = value.trim();
    const texts = { ...terms.texts };
    if (trimmed && trimmed !== fallback) texts[key] = trimmed; else delete texts[key];
    const next = { ...terms, texts };
    setTerms(next);
    gateway.saveLayout(tenantId, TERMS_PAGE_KEY, next).then(setTerms).catch(() => {});
  };

  return (
    <div className="forq-workspace-page">
      <header className="forq-workspace-page__head">
        <div className="forq-workspace-page__heading">
          {editing ? (
            <>
              {eyebrow != null ? (
                <input
                  className="forq-workspace-page__eyebrow-input"
                  defaultValue={shownEyebrow}
                  placeholder={eyebrow}
                  aria-label="Bereichs-Kennzeichnung umbenennen"
                  onBlur={(event) => saveTerm(eyebrowKey, event.target.value, eyebrow)}
                />
              ) : null}
              <input
                className="forq-workspace-page__title-input"
                defaultValue={shownTitle}
                placeholder={title}
                aria-label="Seitentitel umbenennen"
                onBlur={(event) => saveTerm(titleKey, event.target.value, title)}
              />
              {description ? <p>{description}</p> : null}
            </>
          ) : (
            <>
              {shownEyebrow ? <span className="forq-eyebrow">{shownEyebrow}</span> : null}
              <h1>{shownTitle}</h1>
              {description ? <p>{description}</p> : null}
            </>
          )}
        </div>
        {actions ? <div className="forq-workspace-page__actions">{actions}</div> : null}
      </header>
      {metrics ? <div className="forq-metric-row">{metrics}</div> : null}
      {toolbar ? <div className="forq-workspace-page__toolbar">{toolbar}</div> : null}
      <div className="forq-workspace-page__body">{children}</div>
    </div>
  );
}
